import {
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Pin,
  PinOff,
  RotateCcw,
  Settings2,
} from 'lucide-react';
import { useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import RightArrowPurple from '@/assets/member-assets/homepage/purple_arrow_icon.svg';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SidebarMode } from '@/config/sidebar-config';
import { cn } from '@/lib/utils';
import { SidebarNavItem, SIDEBAR_TOOLTIP_CLASSES } from './types';

const isHrefActive = (href: string, currentPath: string, currentSearch: string): boolean => {
  const [hrefPath, hrefQuery] = href.split('?');
  if (hrefPath !== currentPath) return false;
  const current = new URLSearchParams(currentSearch);
  const expected = new URLSearchParams(hrefQuery ?? '');
  if (!expected.has('tab') && current.has('tab')) return false;
  return Array.from(expected.entries()).every(([key, value]) => current.get(key) === value);
};

interface SidebarPinControls {
  pinnedItems: SidebarNavItem[];
  unpinnedItems: SidebarNavItem[];
  pin: (id: string) => void;
  unpin: (id: string) => void;
  moveUp: (id: string) => void;
  moveDown: (id: string) => void;
  resetToDefaults: () => void;
  pinAll: () => void;
  unpinAll: () => void;
}

interface SidebarNavListProps {
  items: SidebarNavItem[];
  pathname: string;
  sidebarMode?: SidebarMode;
  onToggle?: () => void;
  onItemClick?: () => void;
  variant: 'desktop' | 'mobile';
  pinControls?: SidebarPinControls;
  testIdPrefix?: string;
}

const getNavItemTestId = (testIdPrefix: string | undefined, item: SidebarNavItem) => {
  if (!testIdPrefix || !item.id) return undefined;
  return `${testIdPrefix}_navItem_${item.id}`;
};

const getSectionTestId = (testIdPrefix: string | undefined, title?: string) => {
  if (!testIdPrefix || !title) return undefined;
  const sectionKey = title.replace(/\s+/g, '');
  return `${testIdPrefix}_section${sectionKey}`;
};

const NavIcon = ({
  icon,
  className,
}: {
  icon: SidebarNavItem['icon'];
  name: string;
  className?: string;
}) => {
  if (typeof icon === 'string') {
    return <img src={icon} alt="" className={className} />;
  }
  const IconComponent = icon;
  return <IconComponent className={className} aria-hidden="true" />;
};

const compactItemClasses =
  'relative flex w-full min-w-0 flex-col items-center justify-start gap-0.5 rounded-lg px-1 py-1.5 text-center transition-colors duration-150 ease motion-reduce:transition-none';

const compactIconClasses = 'h-5 w-5 flex-none object-contain brightness-0 invert';

const compactLabelClasses =
  'w-full min-w-0 line-clamp-2 break-words text-center text-[11px] font-semibold leading-[14px]';

const MobileNavItem = ({
  item,
  onItemClick,
  testIdPrefix,
}: {
  item: SidebarNavItem;
  onItemClick?: () => void;
  testIdPrefix?: string;
}) => {
  const navItemTestId = getNavItemTestId(testIdPrefix, item);
  if (item.disabled) {
    return (
      <div className="flex items-center justify-between text-gray-400 text-base font-medium py-2.5 px-3 rounded-2xl opacity-50 cursor-not-allowed">
        <div className="flex items-center">
          <NavIcon icon={item.icon} name={item.name} className="mr-4 w-5 h-5" />
          <span>{item.name}</span>
        </div>
        {item.badge && (
          <span className="text-xs font-medium text-gray-400 px-2 py-0.5">{item.badge}</span>
        )}
      </div>
    );
  }

  const sharedClassName =
    'flex items-center justify-between text-gray-800 text-base font-medium py-2.5 px-3 rounded-2xl hover:bg-slate-200 transition-colors duration-150 motion-reduce:transition-none';

  const innerContent = (
    <>
      <div className="flex items-center">
        <NavIcon icon={item.icon} name={item.name} className="mr-4 w-5 h-5" />
        <span>{item.name}</span>
      </div>
      {item.badge && (
        <>
          {item.badgeType === 'arrow' && (
            <div className="flex items-center bg-purple-100 text-purple-700 rounded-full pl-2.5 pr-1 py-1 text-sm font-semibold tabular-nums">
              <span>{item.badge}</span>
              <img src={RightArrowPurple} alt="go" className="ml-1.5 w-4 h-4" />
            </div>
          )}
          {item.badgeType === 'dot' && (
            <span className="w-7 h-7 bg-red-100 text-red-700 text-sm font-semibold rounded-full flex items-center justify-center tabular-nums">
              {item.badge}
            </span>
          )}
        </>
      )}
    </>
  );

  if (item.onAction) {
    return (
      <button
        type="button"
        onClick={() => {
          item.onAction!();
          onItemClick?.();
        }}
        className={cn(sharedClassName, 'w-full text-left')}
        data-testid={navItemTestId}
      >
        {innerContent}
      </button>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={onItemClick}
      className={sharedClassName}
      data-testid={navItemTestId}
    >
      {innerContent}
    </Link>
  );
};

const DesktopNavItem = ({
  item,
  pathname,
  isCollapsed,
  testIdPrefix,
}: {
  item: SidebarNavItem;
  pathname: string;
  isCollapsed: boolean;
  testIdPrefix?: string;
}) => {
  const navItemTestId = getNavItemTestId(testIdPrefix, item);
  const isActive = !item.disabled && !item.onAction && pathname === item.href;
  const isHealthSnapshotFromTodo =
    item.name === 'Health Snapshot' && pathname.includes('/member/todo/');
  const linkTo = isHealthSnapshotFromTodo ? `${item.href}?from=todo` : item.href;

  if (item.disabled) {
    return (
      <div
        className={cn(
          'flex items-center px-3 py-1.5 text-sm font-medium rounded-3xl w-full text-left opacity-30 cursor-not-allowed text-white/50',
          { 'justify-center': isCollapsed }
        )}
        title={isCollapsed ? item.name : item.badge || ''}
      >
        <NavIcon
          icon={item.icon}
          name={item.name}
          className="w-5 h-5 flex-shrink-0 brightness-0 invert opacity-50"
        />
        <span className={cn('ml-3 min-w-0 flex-1 break-words', isCollapsed && 'hidden')}>
          {item.name}
        </span>
        {!isCollapsed && item.badge && (
          <span className="text-xs font-medium text-white/40 px-2 py-0.5">{item.badge}</span>
        )}
      </div>
    );
  }

  const sharedClassName = cn(
    'flex items-center px-3 py-1.5 text-sm font-medium rounded-lg group transition-colors duration-150 ease motion-reduce:transition-none w-full text-left',
    { 'justify-center': isCollapsed },
    isActive
      ? 'bg-white/15 text-white font-semibold'
      : 'text-white/80 hover:bg-white/10 hover:text-white'
  );

  const innerContent = (
    <>
      <NavIcon
        icon={item.icon}
        name={item.name}
        className={cn(
          'w-5 h-5 flex-shrink-0 brightness-0 invert',
          isActive ? 'opacity-100' : 'opacity-80 group-hover:opacity-100'
        )}
      />
      <span className={cn('ml-3 min-w-0 flex-1 break-words', isCollapsed && 'hidden')}>
        {item.name}
      </span>
      {!isCollapsed && item.badge && (
        <span className="text-xs font-semibold bg-white/10 text-white/80 px-2 py-0.5 rounded-3xl tabular-nums">
          {item.badge}
        </span>
      )}
      {!isCollapsed && item.shortcut && (
        <kbd className="ml-auto text-[10px] font-mono bg-white/10 text-white/50 px-1.5 py-0.5 rounded">
          {item.shortcut}
        </kbd>
      )}
    </>
  );

  if (item.onAction) {
    return (
      <button
        type="button"
        onClick={item.onAction}
        className={sharedClassName}
        title={isCollapsed ? item.name : ''}
        data-testid={navItemTestId}
      >
        {innerContent}
      </button>
    );
  }

  return (
    <Link
      to={linkTo}
      className={sharedClassName}
      title={isCollapsed ? item.name : ''}
      onMouseEnter={() => item.onPrefetch?.()}
      data-testid={navItemTestId}
    >
      {innerContent}
    </Link>
  );
};

const CompactNavItem = ({
  item,
  pathname,
  testIdPrefix,
}: {
  item: SidebarNavItem;
  pathname: string;
  testIdPrefix?: string;
}) => {
  const navItemTestId = getNavItemTestId(testIdPrefix, item);
  const isActive = !item.disabled && !item.onAction && pathname === item.href;
  const isHealthSnapshotFromTodo =
    item.name === 'Health Snapshot' && pathname.includes('/member/todo/');
  const linkTo = isHealthSnapshotFromTodo ? `${item.href}?from=todo` : item.href;
  const label = item.compactName ?? item.name;

  if (item.disabled) {
    return (
      <div className={cn(compactItemClasses, 'opacity-30 cursor-not-allowed')}>
        <NavIcon
          icon={item.icon}
          name={item.name}
          className={cn(compactIconClasses, 'opacity-50')}
        />
        <span className={cn(compactLabelClasses, 'text-white/40')}>{label}</span>
      </div>
    );
  }

  const sharedClassName = cn(
    compactItemClasses,
    isActive ? 'text-white bg-white/15' : 'text-white/70 hover:bg-white/10 hover:text-white'
  );

  const innerContent = (
    <>
      {isActive && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-white rounded-r-full" />
      )}
      <NavIcon
        icon={item.icon}
        name={item.name}
        className={cn(compactIconClasses, isActive ? 'opacity-100' : 'opacity-70')}
      />
      <span className={compactLabelClasses}>{label}</span>
    </>
  );

  if (item.onAction) {
    return (
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={item.onAction}
            className={sharedClassName}
            aria-label={item.name}
            data-testid={navItemTestId}
          >
            {innerContent}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8} className={SIDEBAR_TOOLTIP_CLASSES}>
          <div className="space-y-1">
            <p className="text-[13px] font-semibold text-gray-900">{item.name}</p>
            {item.shortcut && (
              <kbd className="inline-block text-[11px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                {item.shortcut}
              </kbd>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <Link
          to={linkTo}
          className={sharedClassName}
          aria-label={item.name}
          data-testid={navItemTestId}
        >
          {isActive && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-white rounded-r-full" />
          )}
          <NavIcon
            icon={item.icon}
            name={item.name}
            className={cn(compactIconClasses, isActive ? 'opacity-100' : 'opacity-70')}
          />
          <span className={compactLabelClasses}>{label}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className={SIDEBAR_TOOLTIP_CLASSES}>
        <div className="space-y-1">
          <p className="text-[13px] font-semibold text-gray-900">{item.name}</p>
          {item.badge && (
            <span className="inline-flex items-center text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
              {item.badge}
            </span>
          )}
          {item.shortcut && (
            <kbd className="inline-block text-[11px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
              {item.shortcut}
            </kbd>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const navigateToHref = (
  href: string,
  currentPath: string,
  navigate: ReturnType<typeof useNavigate>
) => {
  const [targetPath] = href.split('?');
  if (currentPath === targetPath || currentPath.startsWith(targetPath + '/')) {
    window.location.href = href;
  } else {
    navigate(href);
  }
};

const CompactSubmenuItem = ({
  item,
  pathname,
  testIdPrefix,
}: {
  item: SidebarNavItem;
  pathname: string;
  testIdPrefix?: string;
}) => {
  const navItemTestId = getNavItemTestId(testIdPrefix, item);
  const { search } = useLocation();
  const navigate = useNavigate();
  const isParentActive = pathname.startsWith(item.href.split('?')[0]);
  const label = item.compactName ?? item.name;

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={() => {
            if (item.onAction) {
              item.onAction();
            } else {
              navigateToHref(item.href, pathname, navigate);
            }
          }}
          onMouseEnter={() => item.onPrefetch?.()}
          className={cn(
            compactItemClasses,
            'cursor-pointer',
            isParentActive
              ? 'text-white bg-white/15'
              : 'text-white/70 hover:bg-white/10 hover:text-white'
          )}
          aria-label={item.name}
          data-testid={navItemTestId}
        >
          {isParentActive && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-white rounded-r-full" />
          )}
          <NavIcon
            icon={item.icon}
            name={item.name}
            className={cn(compactIconClasses, isParentActive ? 'opacity-100' : 'opacity-70')}
          />
          <span className={compactLabelClasses}>{label}</span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={8}
        className="w-auto min-w-[160px] max-w-[220px] max-h-[var(--radix-hover-card-content-available-height)] overflow-y-auto overscroll-contain p-0"
      >
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-[13px] font-semibold text-gray-900">{item.name}</p>
        </div>
        {item.subItems && (
          <div className="py-1">
            {item.subItems.map((sub) => (
              <button
                key={sub.name}
                type="button"
                onClick={() => navigateToHref(sub.href, pathname, navigate)}
                onMouseEnter={() => item.onPrefetch?.()}
                className={cn(
                  'w-full flex items-center px-3 py-2.5 text-[13px] text-left transition-colors duration-100 cursor-pointer',
                  isHrefActive(sub.href, pathname, search)
                    ? 'text-gray-900 font-medium bg-gray-50'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                )}
              >
                {sub.name}
              </button>
            ))}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

const DesktopSubmenuItem = ({
  item,
  pathname,
  isCollapsed,
  onToggle,
  testIdPrefix,
}: {
  item: SidebarNavItem;
  pathname: string;
  isCollapsed: boolean;
  onToggle?: () => void;
  testIdPrefix?: string;
}) => {
  const navItemTestId = getNavItemTestId(testIdPrefix, item);
  const { search } = useLocation();
  const isParentActive = pathname.startsWith(item.href.split('?')[0]);
  const [isOpen, setIsOpen] = useState(() => isParentActive);

  return (
    <div>
      <button
        onClick={() => {
          if (isCollapsed) onToggle?.();
          setIsOpen((prev) => !prev);
        }}
        onMouseEnter={() => item.onPrefetch?.()}
        className={cn(
          'w-full flex items-center px-3 py-1.5 text-sm font-medium rounded-lg hover:bg-white/10 transition-colors duration-150 ease motion-reduce:transition-none',
          isCollapsed ? 'justify-center' : 'justify-between',
          isParentActive ? 'text-white bg-white/10 font-semibold' : 'text-white/80'
        )}
        title={isCollapsed ? item.name : ''}
        aria-label={item.name}
        aria-expanded={!isCollapsed && isOpen}
        data-testid={navItemTestId}
      >
        <div className="flex min-w-0 items-center">
          <NavIcon
            icon={item.icon}
            name={item.name}
            className={cn(
              'w-5 h-5 flex-shrink-0 brightness-0 invert',
              isParentActive ? 'opacity-100' : 'opacity-70'
            )}
          />
          <span className={cn('ml-3 min-w-0 break-words text-left', isCollapsed && 'hidden')}>
            {item.name}
          </span>
        </div>
        {!isCollapsed &&
          (isOpen ? (
            <ChevronDown className="w-4 h-4 text-white/50" />
          ) : (
            <ChevronRight className="w-4 h-4 text-white/50" />
          ))}
      </button>
      {!isCollapsed && (
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none',
            isOpen ? 'grid-rows-[1fr] mt-1 visible' : 'grid-rows-[0fr] invisible'
          )}
        >
          <div className="overflow-hidden">
            <div className="pl-8 space-y-0.5 py-1">
              {item.subItems?.map((subItem) => (
                <Link
                  key={subItem.name}
                  to={subItem.href}
                  onMouseEnter={() => item.onPrefetch?.()}
                  className={cn(
                    'block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ease motion-reduce:transition-none',
                    isHrefActive(subItem.href, pathname, search)
                      ? 'text-white font-medium bg-white/10'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  )}
                >
                  {subItem.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({
  title,
  isFirst,
  sidebarMode,
  variant,
  testIdPrefix,
}: {
  title?: string;
  isFirst: boolean;
  sidebarMode?: SidebarMode;
  variant: 'desktop' | 'mobile';
  testIdPrefix?: string;
}) => {
  const sectionTestId = getSectionTestId(testIdPrefix, title);
  if (variant === 'desktop' && sidebarMode === 'icon-only') {
    return <div className={cn('border-t border-white/10', isFirst ? 'mt-0' : 'mt-3 mb-2')} />;
  }

  if (variant === 'desktop' && sidebarMode === 'compact') {
    if (isFirst) return null;
    return <div className="mt-3 mb-1" />;
  }

  if (variant === 'desktop') {
    return (
      <div className={cn('pb-2', isFirst ? 'pt-0' : 'pt-5')} data-testid={sectionTestId}>
        <div className="flex items-center gap-2 px-1">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-[10px] font-semibold text-white/40 uppercase tracking-wider whitespace-nowrap">
            {title}
          </span>
          <div className="h-px flex-1 bg-white/10" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('pb-2 px-2', isFirst ? 'pt-0' : 'pt-4')} data-testid={sectionTestId}>
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-gray-300" />
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
          {title}
        </span>
        <div className="h-px flex-1 bg-gray-300" />
      </div>
    </div>
  );
};

const MoreItemRow = ({
  item,
  pathname,
  search,
  navigate,
  onClose,
  action,
}: {
  item: SidebarNavItem;
  pathname: string;
  search: string;
  navigate: ReturnType<typeof useNavigate>;
  onClose: () => void;
  action?: React.ReactNode;
}) => {
  const Icon = typeof item.icon === 'string' ? null : item.icon;
  const basePath = item.href.split('?')[0];
  const isItemActive = pathname.startsWith(basePath);
  const hasSubItems = item.subItems && item.subItems.length > 0;

  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center group/row">
        <button
          type="button"
          onClick={() => {
            if (item.onAction) {
              item.onAction();
              onClose();
            } else {
              navigateToHref(item.href, pathname, navigate);
              if (!hasSubItems) onClose();
            }
          }}
          className={cn(
            'flex-1 flex items-center gap-2.5 px-3 py-2 text-[13px] text-left transition-colors duration-100 cursor-pointer',
            isItemActive
              ? 'text-gray-900 font-medium bg-gray-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          )}
        >
          {Icon && (
            <Icon
              className={cn(
                'w-4 h-4 flex-shrink-0',
                isItemActive ? 'text-gray-900' : 'text-gray-400'
              )}
            />
          )}
          {typeof item.icon === 'string' && (
            <img src={item.icon} alt="" className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="flex-1 truncate">{item.compactName ?? item.name}</span>
        </button>
        {action && <div className="flex items-center pr-1.5">{action}</div>}
      </div>
      {hasSubItems && isItemActive && (
        <div className="ml-5 border-l border-gray-100">
          {item.subItems!.map((sub) => (
            <button
              key={sub.name}
              type="button"
              onClick={() => {
                navigateToHref(sub.href, pathname, navigate);
                onClose();
              }}
              className={cn(
                'w-full flex items-center pl-4 pr-3 py-1.5 text-[12px] text-left transition-colors duration-100 cursor-pointer',
                isHrefActive(sub.href, pathname, search)
                  ? 'text-gray-900 font-medium'
                  : 'text-gray-400 hover:text-gray-700'
              )}
            >
              {sub.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CompactMoreButton = ({
  pinnedItems,
  unpinnedItems,
  pathname,
  pinControls,
  testIdPrefix,
}: {
  pinnedItems: SidebarNavItem[];
  unpinnedItems: SidebarNavItem[];
  pathname: string;
  pinControls?: SidebarPinControls;
  testIdPrefix?: string;
}) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>();

  const hasActiveChild = unpinnedItems.some((item) => {
    if (item.href === '#') return false;
    const basePath = item.href.split('?')[0];
    if (pathname.startsWith(basePath)) return true;
    return item.subItems?.some((sub) => isHrefActive(sub.href, pathname, search));
  });

  const onClose = () => setIsOpen(false);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setIsOpen(true), 120);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverTimeout.current);
    if (!isEditing) {
      hoverTimeout.current = setTimeout(() => setIsOpen(false), 200);
    }
  }, [isEditing]);

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        clearTimeout(hoverTimeout.current);
        setIsOpen(open);
        if (!open) setIsEditing(false);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={cn(
            compactItemClasses,
            'cursor-pointer',
            hasActiveChild
              ? 'text-white bg-white/15'
              : 'text-white/70 hover:bg-white/10 hover:text-white'
          )}
          data-testid={testIdPrefix ? `${testIdPrefix}_moreMenuBtn` : undefined}
        >
          {hasActiveChild && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-white rounded-r-full" />
          )}
          <MoreHorizontal
            className={cn('h-5 w-5 flex-none', hasActiveChild ? 'opacity-100' : 'opacity-70')}
          />
          <span className={compactLabelClasses}>More</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={8}
        className="z-[200] flex max-h-[var(--radix-popover-content-available-height)] w-64 flex-col overflow-hidden rounded-xl border border-gray-100 bg-white/95 backdrop-blur-md p-0 shadow-lg"
        onMouseEnter={() => clearTimeout(hoverTimeout.current)}
        onMouseLeave={handleMouseLeave}
      >
        <div className="shrink-0 border-b border-gray-100">
          <div className="flex items-center justify-between gap-2 px-3 py-2.5">
            <span className="min-w-0 truncate text-xs font-semibold uppercase tracking-wider text-gray-500">
              {isEditing ? 'Customize Sidebar' : 'More'}
            </span>
            {pinControls && (
              <button
                type="button"
                onClick={() => setIsEditing((v) => !v)}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors duration-150 cursor-pointer',
                  isEditing
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
                data-testid={testIdPrefix ? `${testIdPrefix}_moreCustomizeBtn` : undefined}
              >
                <Settings2 className="h-3 w-3 shrink-0" />
                {isEditing ? 'Done' : 'Customize'}
              </button>
            )}
          </div>
          {isEditing && pinControls && (unpinnedItems.length > 0 || pinnedItems.length > 0) && (
            <div className="flex items-center gap-1.5 px-3 pb-2.5">
              {unpinnedItems.length > 0 && (
                <button
                  type="button"
                  onClick={() => pinControls.pinAll()}
                  className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600 transition-colors duration-150 cursor-pointer hover:bg-gray-200"
                >
                  <Pin className="h-3 w-3 shrink-0" />
                  Pin all
                </button>
              )}
              {pinnedItems.length > 0 && (
                <button
                  type="button"
                  onClick={() => pinControls.unpinAll()}
                  className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600 transition-colors duration-150 cursor-pointer hover:bg-gray-200"
                >
                  <PinOff className="h-3 w-3 shrink-0" />
                  Unpin all
                </button>
              )}
            </div>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {isEditing && pinControls ? (
            <>
              <div className="px-3 pt-2 pb-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Pinned
                  </span>
                  <button
                    type="button"
                    onClick={() => pinControls.resetToDefaults()}
                    className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5 cursor-pointer"
                  >
                    <RotateCcw className="w-2.5 h-2.5" /> Reset
                  </button>
                </div>
              </div>
              <div className="py-0.5">
                {pinnedItems.map((item, idx) => (
                  <div key={item.id} className="flex items-center group/pin">
                    <div className="flex flex-col pl-1.5">
                      <button
                        type="button"
                        onClick={() => pinControls.moveUp(item.id!)}
                        aria-label={`Move ${item.name} up`}
                        disabled={idx === 0}
                        className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20 cursor-pointer disabled:cursor-default"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => pinControls.moveDown(item.id!)}
                        aria-label={`Move ${item.name} down`}
                        disabled={idx === pinnedItems.length - 1}
                        className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20 cursor-pointer disabled:cursor-default"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <MoreItemRow
                      item={item}
                      pathname={pathname}
                      search={search}
                      navigate={navigate}
                      onClose={onClose}
                      action={
                        <button
                          type="button"
                          onClick={() => pinControls.unpin(item.id!)}
                          className="p-1 text-gray-400 hover:text-red-500 cursor-pointer"
                          title="Unpin from sidebar"
                        >
                          <PinOff className="w-3.5 h-3.5" />
                        </button>
                      }
                    />
                  </div>
                ))}
              </div>
              {unpinnedItems.length > 0 && (
                <>
                  <div className="px-3 pt-2 pb-1 border-t border-gray-100">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      Available
                    </span>
                  </div>
                  <div className="py-0.5">
                    {unpinnedItems.map((item) => (
                      <MoreItemRow
                        key={item.id}
                        item={item}
                        pathname={pathname}
                        search={search}
                        navigate={navigate}
                        onClose={onClose}
                        action={
                          <button
                            type="button"
                            onClick={() => pinControls.pin(item.id!)}
                            className="p-1 text-gray-400 hover:text-primary cursor-pointer"
                            title="Pin to sidebar"
                          >
                            <Pin className="w-3.5 h-3.5" />
                          </button>
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="py-1">
                {unpinnedItems.map((item) => (
                  <MoreItemRow
                    key={item.id ?? item.name}
                    item={item}
                    pathname={pathname}
                    search={search}
                    navigate={navigate}
                    onClose={onClose}
                  />
                ))}
              </div>
              {pinControls && (
                <div className="px-3 py-2 border-t border-gray-100">
                  <p className="text-[11px] text-gray-400 text-center">
                    Tap <span className="font-semibold text-gray-500">Customize</span> to pin,
                    reorder, or hide items
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const SidebarNavList = ({
  items,
  pathname,
  sidebarMode = 'full',
  onToggle,
  onItemClick,
  variant,
  pinControls,
  testIdPrefix,
}: SidebarNavListProps) => {
  const isCollapsed = sidebarMode === 'icon-only';
  const isCompact = sidebarMode === 'compact';

  if (isCompact && pinControls) {
    return (
      <>
        {pinControls.pinnedItems.map((item) => {
          if (item.subItems) {
            return (
              <CompactSubmenuItem
                key={item.id ?? item.name}
                item={item}
                pathname={pathname}
                testIdPrefix={testIdPrefix}
              />
            );
          }
          return (
            <CompactNavItem
              key={item.id ?? item.name}
              item={item}
              pathname={pathname}
              testIdPrefix={testIdPrefix}
            />
          );
        })}
        <CompactMoreButton
          pinnedItems={pinControls.pinnedItems}
          unpinnedItems={pinControls.unpinnedItems}
          pathname={pathname}
          pinControls={pinControls}
          testIdPrefix={testIdPrefix}
        />
      </>
    );
  }

  if (isCompact) {
    const realItems = items.filter((i) => !i.isSectionHeader && !i.isSeparator);
    const primaryItems = realItems.filter((i) => i.compactGroup !== 'more');
    const moreItems = realItems.filter((i) => i.compactGroup === 'more');
    return (
      <>
        {primaryItems.map((item) => {
          if (item.subItems) {
            return (
              <CompactSubmenuItem
                key={item.name}
                item={item}
                pathname={pathname}
                testIdPrefix={testIdPrefix}
              />
            );
          }
          return (
            <CompactNavItem
              key={item.name}
              item={item}
              pathname={pathname}
              testIdPrefix={testIdPrefix}
            />
          );
        })}
        <CompactMoreButton
          pinnedItems={primaryItems}
          unpinnedItems={moreItems}
          pathname={pathname}
          testIdPrefix={testIdPrefix}
        />
      </>
    );
  }

  return (
    <>
      {items.map((item, index) => {
        if (item.isSectionHeader) {
          return (
            <SectionHeader
              key={item.name}
              title={item.sectionTitle}
              isFirst={index === 0}
              sidebarMode={sidebarMode}
              variant={variant}
              testIdPrefix={testIdPrefix}
            />
          );
        }

        if (item.isSeparator) {
          return (
            <div
              key={item.name}
              className={cn(
                'border-t my-2',
                variant === 'desktop' ? 'border-white/10 my-3' : 'border-gray-300'
              )}
            />
          );
        }

        if (variant === 'mobile') {
          return (
            <MobileNavItem
              key={item.name}
              item={item}
              onItemClick={onItemClick}
              testIdPrefix={testIdPrefix}
            />
          );
        }

        if (item.subItems) {
          return (
            <DesktopSubmenuItem
              key={item.name}
              item={item}
              pathname={pathname}
              isCollapsed={isCollapsed}
              onToggle={onToggle}
              testIdPrefix={testIdPrefix}
            />
          );
        }

        return (
          <DesktopNavItem
            key={item.name}
            item={item}
            pathname={pathname}
            isCollapsed={isCollapsed}
            testIdPrefix={testIdPrefix}
          />
        );
      })}
    </>
  );
};
